import React, { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

/*
 * PwaBoot
 * -------
 * A brief animated splash overlay that fades in on first paint when the
 * app is running in standalone (installed) PWA mode. iOS ignores the
 * manifest's animated splash instructions and uses only static PNGs, so
 * this overlay gives the perception of a cinematic boot without breaking
 * that constraint. Displays only once per page load, only in standalone.
 *
 * Wordmark uses the same idle champagne-shimmer sweep as the Nav
 * wordmark. Overlay dissolves after ~1.6s.
 */
export default function PwaBoot() {
  const reduced = useReducedMotion();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator.standalone === true;
    if (!isStandalone) return undefined;
    // Only show once per page load; already loaded pages skip re-runs
    setVisible(true);
    const t = setTimeout(() => setVisible(false), reduced ? 400 : 1600);
    return () => clearTimeout(t);
  }, [reduced]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="pwa-boot"
          role="presentation"
          data-testid="pwa-boot"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-0 z-[70] flex items-center justify-center pointer-events-none"
          style={{ background: "#000000" }}
        >
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            {/* soft champagne bloom behind the wordmark */}
            <span
              aria-hidden
              className="absolute inset-0 -m-16 pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse at center, rgba(201,169,97,0.18), transparent 60%)",
                filter: "blur(28px)",
              }}
            />
            {/* wordmark */}
            <p
              className="relative font-serif text-ink text-center leading-none"
              style={{
                fontFamily: "'Fraunces', serif",
                fontSize: "clamp(2.4rem, 8vw, 4rem)",
                letterSpacing: "-0.02em",
                fontWeight: 500,
              }}
            >
              Get Virtual{" "}
              <span className="italic" style={{ color: "#C9A961" }}>
                Growth
              </span>
            </p>
            {/* champagne shimmer sweep — same easing as Nav idle shimmer */}
            {!reduced && (
              <motion.span
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(100deg, transparent 30%, rgba(245,216,150,0.55) 50%, transparent 70%)",
                  mixBlendMode: "screen",
                  backgroundSize: "220% 100%",
                }}
                initial={{ backgroundPosition: "200% 0" }}
                animate={{ backgroundPosition: "-60% 0" }}
                transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
              />
            )}
            {/* soft champagne underline hairline */}
            <motion.span
              aria-hidden
              className="absolute left-1/2 -translate-x-1/2 mt-4 block h-px"
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: "70%", opacity: 1 }}
              transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              style={{
                bottom: "-14px",
                background:
                  "linear-gradient(90deg, transparent, rgba(201,169,97,0.75), transparent)",
              }}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
